<script lang="ts">import { prefersReducedMotion } from 'svelte/motion';
import emblaCarouselSvelte from 'embla-carousel-svelte';
import AutoScroll from 'embla-carousel-auto-scroll';
import MediaControls from '../MediaControls/MediaControls.svelte';
// prettier-ignore
const { showControls = true, slides = [], slide: slideFromProps, class: className = '', ...rest } = $props();
let emblaApi;
let autoScroll;
let indicesNotInView = $state([]);
let marqueeElement;
let marqueeSlidesElement;
let observer;
let isPlaying = $state(true);
// Variable to keep track of the slides that are being duplicated if there were not enough to fill up the width of the
// screen
// Made any[] because SvelteComponent[] is going to be removed in https://block.atlassian.net/browse/PWB-1297
let duplicatedSlides = $state(slides ?? []);
// Variable to keep track if the duplicatedSlides is enough to fill up the width of the screen
let hasEnoughSlides = false;
function handleMediaControlClick() {
    if (isPlaying) {
        // @ts-expect-error: Embla has autoScroll.stop as an unknown type, so ignoring until it is fixed
        autoScroll.stop();
        isPlaying = false;
        // If someone pressed the control to pause the video, then the marquee should not play again even if the component
        // leaves and reenters the window
        if (observer) {
            observer.disconnect();
        }
    }
    else {
        // @ts-expect-error: Embla has autoScroll.play as an unknown type, so ignoring until it is fixed
        autoScroll.play();
        isPlaying = true;
        // Reconnect the Intersection Observer, so the marquee pauses when it leaves the window
        if (marqueeElement && observer) {
            observer.observe(marqueeElement);
        }
    }
}
function handleVisibilityChange(entries) {
    entries.forEach((entry) => {
        // https://www.embla-carousel.com/plugins/auto-scroll/#methods
        if (entry.isIntersecting) {
            // @ts-expect-error: Embla has autoScroll.play as an unknown type, so ignoring until it is fixed
            autoScroll.play();
            isPlaying = true;
        }
        else {
            // @ts-expect-error: Embla has autoScroll.stop as an unknown type, so ignoring until it is fixed
            autoScroll.stop();
            isPlaying = false;
        }
    });
}
function slidesNotInView(emblaApi) {
    indicesNotInView = emblaApi.slidesNotInView();
}
// The viewport width may be larger than the Marquee width, so this function duplicates the slides array until there
// are enough copies to fill the viewport. Unlike Swiper.js or other heavier libraries, Embla does not handle this
// automatically
function updateSlidesForLooping() {
    if (!marqueeElement || !marqueeSlidesElement || hasEnoughSlides) {
        return;
    }
    const marqueeWidth = marqueeElement.offsetWidth;
    const marqueeSlidesWidth = marqueeSlidesElement.offsetWidth;
    // Since the width of the marqueeElement and the marqueeSlidesElement is known, then the amount of slides needed to
    // be duplicated is also known
    const copiesNeeded = Math.ceil(marqueeWidth / marqueeSlidesWidth);
    const currentCopies = duplicatedSlides.length / slides.length;
    // Only append items to the DOM and never remove items to prevent unnecessary mounting and unmounting of DOM nodes
    if (copiesNeeded > currentCopies) {
        duplicatedSlides = Array.from({ length: copiesNeeded }, () => slides).flat();
        // This prevents onInit from calling resize that would result in an infinite loop
        hasEnoughSlides = true;
    }
}
function onInit(event) {
    emblaApi = event.detail;
    autoScroll = emblaApi?.plugins()?.autoScroll;
    slidesNotInView(emblaApi);
    emblaApi.on('slidesInView', slidesNotInView);
    updateSlidesForLooping();
    // If someone resizes the screen, then we need to restart the AutoScroll plugin because Embla is recalculating the
    // slide positions but the plugin is not reflecting that change. Without watching for resize there will be a gap
    // in the scrolling effect
    emblaApi.on('resize', () => {
        slidesNotInView(emblaApi);
        hasEnoughSlides = false;
        updateSlidesForLooping();
        if (autoScroll && isPlaying) {
            // @ts-expect-error: Embla has autoScroll.play as an unknown type, so ignoring until it is fixed
            autoScroll.play();
        }
        if (observer) {
            observer.disconnect();
            observer.observe(marqueeElement);
        }
    });
    if (marqueeElement && autoScroll && !prefersReducedMotion.current) {
        observer = new IntersectionObserver(handleVisibilityChange, {
            threshold: 0,
        });
        observer.observe(marqueeElement);
    }
}
// https://www.embla-carousel.com/api/options/
const options = {
    // Align needs to be set to start instead of defaulting to center because Embla recalculates the position of the
    // marquee since it's a loop and start ensures it starts at the beginning
    align: 'start',
    loop: true,
    watchDrag: false,
};
// https://www.embla-carousel.com/plugins/
const plugins = [
    // https://www.embla-carousel.com/plugins/auto-scroll/
    AutoScroll({
        speed: 1.17,
        startDelay: 0,
        playOnInit: false,
    }),
];
</script>

<div {...rest} class="Marquee {className}" bind:this={marqueeElement}>
  <div
    use:emblaCarouselSvelte={{ options, plugins }}
    onemblaInit={onInit}
    role={showControls ? 'region' : 'marquee'}
    aria-roledescription={showControls ? 'carousel' : undefined}
    aria-label="Scrolling marquee"
  >
    <ul class="Marquee__slides" bind:this={marqueeSlidesElement}>
      {#each duplicatedSlides as slide, idx}
        <!-- The aria-label should still be slides and not duplicatedSlides because the extra duplicated ones should not
         have unique numbers since it is the same content -->
        <li
          class="Marquee__slide"
          aria-label="Slide {(idx % slides.length) + 1} of {slides.length}"
          aria-hidden={indicesNotInView.includes(idx)}
        >
          {@render slideFromProps?.({ slide })}
        </li>
      {/each}
    </ul>
  </div>

  {#if showControls}
    <MediaControls {isPlaying} onClick={handleMediaControlClick} />
  {/if}
</div>

<style>/**
 * Do not edit directly, this file was auto-generated.
 * Wed, 16 Jul 2025 20:39:39 GMT
 */
/**
 We use rem units for media queries to promote accessible layouts. By utilizing rem units, the computed values in media queries are determined by the user's browser-defined font size rather than our global 'font-size: 62.5%' reset. This approach ensures that our design adapts to individual user settings, enhancing accessibility. 
 */
@layer design-system-components.pwds {
  .Marquee {
    position: relative;
    overflow: hidden;
  }
  .Marquee__slides, .Marquee__slide {
    will-change: transform;
  }
  .Marquee__slides {
    display: inline-flex;
    margin-top: var(--space-none);
    margin-bottom: var(--space-none);
  }
  .Marquee__slide {
    flex-shrink: 0;
    padding-left: var(--space-30);
  }
  @media (min-width: 64rem) {
    .Marquee__slide {
      padding-left: var(--space-60);
    }
  }
  .Marquee__slide:has(:global(img)) {
    padding-left: var(--space-20);
  }
  .Marquee__slide :global(*:last-child) {
    margin-bottom: var(--space-none);
  }
  .Marquee :global(img) {
    height: var(--marquee-image-height);
    width: auto;
    pointer-events: none;
    user-select: none;
  }
  .Marquee :global(.MediaControls) {
    position: absolute;
    bottom: var(--space-20);
    right: var(--space-20);
  }
}</style>
